import { Alert, Typography, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { TranscriptionService } from '@klangio/ai2notes-ts-client';

import { AppConstantsContext, UserContext } from '../../../context';
import type { TranscriptionDialogContextValue } from '../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import withTranscriptionDialogContextProvider from '../../../context/TranscriptionDialogContext/withTranscriptionDialogContextProvider';
import { TranscriptionStoreContext } from '../../../context/TranscriptionStoreContext/TranscriptionStoreContext';
import type { ExportType } from '../../../entities';
import { TranscriptionState } from '../../../entities';
import { TrackingAction, TrackingCategory, trackingService } from '../../../services';
import type { ITheme } from '../../../theme';
import { AUTH_URL } from '../../../utils';
import DownloadProgressAlert from '../../molecules/DownloadProgressAlert';
import SkeletonWrapper from '../../molecules/SkeletonWrapper';
import PricingTableModal from '../../templates/PricingTableModal';
import { UserDialog } from '../Dialogs';
import InputContainerModal from '../InputContainer/InputContainerModal';
import InputButton from './InputButton';
import Songbook from './Songbook/Songbook';
import SongbookSkeleton from './Songbook/Songbook.skeleton';
import SongCard from './songCard/SongCard';
import SongCardSkeleton from './songCard/SongCard.skeleton';
import UserBox from './UserBox';
import { useUserDialog } from '../../../hooks';

const useStyles = makeStyles()((theme) => ({
  main: {
    height: '100%',
    margin: 'auto auto',
    width: '100%',
    maxWidth: '1300px',
  },
  container: {
    maxWidth: '94em',
  },
  content: {
    maxWidth: '1300px',
    margin: '0 1em',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  headContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    justifyContent: 'space-between',
    gap: '4rem',
    margin: '1rem 0',

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gap: '1rem',
      position: 'fixed',
      bottom: 0,
      width: '100%',
      zIndex: 1000,
      margin: '0',
      left: 0,
    },
  },
  songbookContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',

    gap: '4rem',
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      marginBottom: '4em',
    },
  },
}));

export interface DashboardProps extends TranscriptionDialogContextValue {
  preview?: any;
}

const Dashboard: React.FC<DashboardProps> = ({ preview }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { user, refresh, isLoadingUserCompleted, hasTicketsForUse } = useContext(UserContext);
  const { isDialogOpen, setOpenDialog, onSaveChanges } = useUserDialog();
  // const [uploadProgress, setUploadProgress] = useState<number>(100);
  const { wpLandingPage, isMusehub, musehubPurchaseUrl } = useContext(AppConstantsContext);
  const authenticated = !!user;
  const [openInputContainer, setOpenInputContainer] = useState(false);
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('md'));
  const {
    selectedTranscription,
    setSelectedTranscription,
    updateTranscriptions,
    updatePendingTranscriptions,
    loading,
    transcriptions,
    loadingCompleted,
    error,
  } = useContext(TranscriptionStoreContext);
  const [showPricingModal, setShowPricingModal] = useState(false);

  useEffect(() => {
    if (isLoadingUserCompleted && !authenticated) {
      if (window.location.href.includes('/my-files')) {
        window.location.href = `${AUTH_URL}login?redirect_url=${window.location.href}`;
      } else {
        window.location.href = wpLandingPage;
      }
    }
  }, [isLoadingUserCompleted, authenticated, updateTranscriptions, wpLandingPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      transcriptions.forEach((transcription) => {
        if (
          transcription.state === TranscriptionState.Enqueued
          || transcription.state === TranscriptionState.Processing
        ) {
          updatePendingTranscriptions();
        }
      });
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [transcriptions]);

  useEffect(() => {
    if (authenticated) {
      refresh().then(() => {
        updateTranscriptions();
      });
    }
  }, [authenticated]);

  useEffect(() => {
    if (!selectedTranscription && transcriptions.length > 0 && !isMobile) {
      setSelectedTranscription(transcriptions[0]);
    }
  }, [selectedTranscription, transcriptions]);

  const handleUnlockTranscription = (option: ExportType) => {
    if (hasTicketsForUse) {
      TranscriptionService.transcriptionUnlock(selectedTranscription.id).then((_res) => {
        // res.jobId ?
        setTimeout(() => {
          updateTranscriptions();
          refresh();
        }, 1000);
      });
    } else if (isMusehub) {
      console.log('Open musehub link: ', musehubPurchaseUrl);
      window.open(musehubPurchaseUrl, '_blank');
    } else {
      setShowPricingModal(true);
    }
    const trackingValues = {
      export_type: option,
      location: 'songbook',
      ui_element: 'download',
      pricingModal: selectedTranscription.demo && !hasTicketsForUse,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Buy,
      TrackingAction.BUY_UNLOCK,
      trackingValues,
    );
    console.log(TrackingCategory.Buy, TrackingAction.BUY_UNLOCK, trackingValues);
  };

  if (!authenticated || !isLoadingUserCompleted) {
    return null;
  }
  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <div className={classes.content}>
          <div className={classes.headContainer}>
            <InputButton openDialog={() => setOpenInputContainer(true)} />
            {!isMobile && !isMusehub && <UserBox openUserMenu={() => setOpenDialog(true)} />}
          </div>
          {/* {uploadProgress < 100 && <UploadProgress progress={uploadProgress} />} */}
          <DownloadProgressAlert />
          {error && (
            <Alert severity="error" data-testid="error-alert">
              <Typography>{t('common.error')}</Typography>
            </Alert>
          )}
          <div className={classes.songbookContainer}>
            <SkeletonWrapper
              key="songbook-skeleton"
              loading={loading || !loadingCompleted}
              skeleton={<SongbookSkeleton />}
            >
              <Songbook transcriptions={transcriptions} />
            </SkeletonWrapper>
            <SkeletonWrapper
              key="songcard-skeleton"
              loading={loading || !loadingCompleted}
              skeleton={<SongCardSkeleton />}
            >
              {transcriptions.length !== 0 && loadingCompleted && (
                <SongCard
                  key={selectedTranscription?.id}
                  handleUnlockTranscription={handleUnlockTranscription}
                  preview={preview}
                />
              )}
            </SkeletonWrapper>
          </div>
        </div>

        {openInputContainer && (
          <InputContainerModal modalAsDrawer handleClose={() => setOpenInputContainer(false)} />
        )}
        {isDialogOpen && (
          <UserDialog
            open={isDialogOpen}
            handleClose={() => setOpenDialog(false)}
            onSave={onSaveChanges}
          />
        )}
      </div>
      {showPricingModal && (
        <PricingTableModal
          transcription={selectedTranscription}
          open={showPricingModal}
          handleClose={() => setShowPricingModal(false)}
        />
      )}
    </div>
  );
};

export default withTranscriptionDialogContextProvider(Dashboard);
